import {
  Bookmark,
  FolderGit2,
  Clock3,
  Crown,
  Database,
  FileText,
  LifeBuoy,
  Mail,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Star,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Component, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  confirmAdminVipOrder,
  generateAllTimeInsights,
  generateDailyRanking,
  getAdminAllTimeRanking,
  getAdminAnalyticsOverview,
  getAdminAnalyticsTopRepos,
  getAdminDailyRanking,
  getAdminFeedback,
  getAdminSupportTickets,
  getAdminUserMetrics,
  getAdminUsers,
  getAdminV2Analytics,
  getAdminVipLogs,
  getAdminVipMemberships,
  getAdminVipOrders,
  getAdminWeeklyRanking,
  getRankingHealth,
  getRankingJobs,
  getRankingMetrics,
  getTools,
  importAllTimeProjects,
  markRefundedAdminSupportTicket,
  markRefundedAdminVipOrder,
  publishDailyRanking,
  publishWeeklyRanking,
  refreshAllTimeRanking,
  refreshWeeklyRanking,
  rejectAdminSupportTicket,
  rejectAdminVipOrder,
  resolveAdminSupportTicket,
  updateAdminSupportTicket,
  updateAdminUserMembership,
  archiveAdminProjectTemplate,
  convertAdminSourceItemToTemplate,
  createAdminProjectTemplate,
  getAdminExternalSourceItems,
  getAdminProjectTemplates,
  importAdminGithubSourceItems,
  importAdminOpenAlexSourceItems,
  importAdminProjectSourceUrl,
  updateAdminProjectTemplate,
} from '../api.js'
import EmptyState from '../components/EmptyState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import Pagination from '../components/Pagination.jsx'
import { StatsCard } from '../components/ui/index.js'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'
import { shanghaiDate } from '../ranking-display.js'
import { displayValue, normalizeDisplayText } from '../utils/adminDisplay.js'

const TABS = [
  ['overview', '后台首页'],
  ['support', '客服待办'],
  ['vipOrders', '订单审核'],
  ['studentGrowthStats', '成长运营'],
]

const ADMIN_GROUPS = [
  {
    title: '用户与权限',
    tabs: [
      ['users', '用户管理'],
      ['vip', 'VIP 管理'],
    ],
  },
  {
    title: '商业与客服',
    tabs: [
      ['vipOrders', 'VIP 订单'],
      ['support', '客服与退款'],
      ['feedback', '用户反馈'],
    ],
  },
  {
    title: '学生成长运营',
    tabs: [
      ['studentGrowthStats', '成长计划统计'],
      ['studentGrowthFeedback', '学生反馈'],
      ['studentPracticeRecords', '实训打卡记录'],
      ['studentCompetitionStats', '竞赛/证书数据'],
    ],
  },
  {
    title: '内容与成果',
    tabs: [
      ['projectTemplates', '项目成长计划模板'],
      ['daily', '每日榜管理'],
      ['weekly', '每周榜管理'],
      ['allTime', '总榜管理'],
      ['repos', 'GitHub 项目状态'],
    ],
  },
  {
    title: '系统与数据',
    tabs: [
      ['operations', '运营数据'],
      ['analytics', 'AI 分析状态'],
      ['v2', 'V2 统计'],
      ['developerTools', '开发者工具'],
      ['jobs', '任务日志'],
      ['health', '系统状态'],
      ['settings', '系统设置'],
    ],
  },
]

const STATUS_LABELS = {
  draft: '草稿',
  ready: '待发布',
  submitted: '待审核',
  pending: '待处理',
  processing: '处理中',
  published: '已发布',
  paid: '已开通',
  success: '同步成功',
  completed: '已分析',
  fallback: '已分析',
  running: '分析中',
  failed: '发布失败',
  rejected: '已驳回',
  refunded: '已退款',
  refund_requested: '申请退款',
  open: '待处理',
  resolved: '已解决',
  ignored: '已忽略',
  skipped: '已跳过',
}

const JOB_LABELS = {
  daily_generate: '每日榜生成',
  daily_publish: '每日榜发布',
  weekly_refresh: '每周榜刷新',
  all_time_refresh: '总榜刷新',
  all_time_import: '总榜项目导入',
  all_time_insights: '总榜 AI 分析',
}

const SUPPORT_TYPE_LABELS = {
  activation_failed: '开通失败',
  duplicate_payment: '重复付款',
  refund_request: '退款申请',
  feature_bug: '功能异常',
  quota_issue: '额度问题',
  other: '其他',
}

const MODULE_DESCRIPTIONS = {
  overview: '查看网站运行情况、用户数据、项目发布和 AI 分析状态。',
  users: '查看用户账号、会员状态、使用次数和权限。',
  vip: '查看会员用户、权限状态、到期时间和操作记录。',
  vipOrders: '审核 VIP 订单、付款截图和退款状态。',
  support: '处理客服工单、退款申请和用户备注。',
  feedback: '查看用户反馈内容、联系方式和页面来源。',
  daily: '查看项目的 AI 解析状态、发布状态和每日榜数据。',
  weekly: '查看项目的 AI 解析状态、发布状态和每周榜数据。',
  allTime: '查看项目的 AI 解析状态、发布状态和总榜数据。',
  repos: '查看 GitHub 项目发布状态和 AI 分析状态。',
  analytics: '查看 AI 分析完成度、榜单健康度和接口状态。',
  studentGrowthStats: '查看学生成长计划、任务完成情况、竞赛证书和用户反馈。',
  studentGrowthFeedback: '查看学生端提交的反馈和成长相关建议。',
  studentPracticeRecords: '查看学生实训打卡记录和任务完成情况。',
  studentCompetitionStats: '查看竞赛推荐、证书推荐、使用人数和完成次数。',
  projectTemplates: '管理项目经验模板、外部来源素材和待审核草稿。',
  operations: '查看访问、收藏、订阅和工具点击数据。',
  v2: '查看新版工具页的用户行为统计。',
  developerTools: '查看开发者工具数据和跳转入口。',
  jobs: '查看后台任务执行结果。',
  health: '查看系统状态和接口健康情况。',
  settings: '查看系统设置入口。',
}

function number(value) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed) ? new Intl.NumberFormat('zh-CN').format(parsed) : '0'
}

function compactNumber(value) {
  const parsed = Number(value || 0)
  return Number.isFinite(parsed)
    ? new Intl.NumberFormat('zh-CN', { notation: 'compact', maximumFractionDigits: 1 }).format(parsed)
    : '0'
}

function dateTime(value) {
  if (!value) return '未设置'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return displayValue(value, '未知')
  return parsed.toLocaleString('zh-CN', { hour12: false })
}

function dateOnly(value) {
  if (!value) return '未设置'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value).slice(0, 10)
  return parsed.toISOString().slice(0, 10)
}

function vipPlanLabel(value) {
  const text = String(value || '').trim()
  if (!text) return '普通用户'
  if (text === 'monthly') return '月卡VIP'
  if (text === 'quarterly') return '季卡VIP'
  if (text === 'yearly') return '年卡VIP'
  return text.includes('VIP') ? text : `${text} VIP`
}

function userVipSummary(user) {
  if (user.vipStatus === 'active' || user.membership_tier === 'vip') {
    const plan = vipPlanLabel(user.vipPlanName || user.vipPlanId || user.membershipPlanName)
    const expiresAt = user.vipExpiresAt || user.membership_expires_at
    return (
      <div className="admin-user-membership">
        <strong>{plan}</strong>
        <span>{dateOnly(expiresAt)}到期</span>
      </div>
    )
  }
  return '普通用户'
}

function safeArray(value) {
  return Array.isArray(value) ? value : []
}

function pagePayload(value) {
  return {
    items: safeArray(value?.items),
    pagination: value?.pagination || null,
  }
}

function statusText(value, fallback = '待处理') {
  if (!value) return fallback
  return STATUS_LABELS[value] || displayValue(value, fallback)
}

function errorMessage(error) {
  if (error?.name === 'AbortError') return ''
  if (error?.status === 401 || error?.status === 403) return '无权限访问或登录已过期，请重新登录。'
  if (error?.status === 404) return '接口路径不一致，请检查前后端版本。'
  if (error?.status >= 500) return '后台服务异常，请稍后再试。'
  if (error?.code === 'NETWORK_ERROR' || error?.status === 0) return '接口加载失败，请稍后重试。'
  return displayValue(error?.message, '数据加载失败')
}

function getRepoName(repo) {
  return displayValue(repo?.fullName || repo?.full_name || repo?.name, '未命名项目')
}

function getRepoStatus(repo) {
  if (repo?.publishedAt || repo?.published_at || repo?.status === 'published') return '已发布'
  if (repo?.status === 'failed') return '发布失败'
  return statusText(repo?.status, '待发布')
}

function getAiStatus(repo) {
  const value = repo?.aiStatus || repo?.ai_status || repo?.insightStatus || repo?.analysisStatus
  if (value === 'completed') return '已分析'
  if (value === 'running' || value === 'processing') return '分析中'
  if (value === 'failed') return '分析失败'
  return '未分析'
}

class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    console.error('[admin-page]', error)
  }

  render() {
    if (this.state.hasError) {
      return <div className="admin-notice error" role="alert">后台模块加载失败，请刷新或稍后再试。</div>
    }
    return this.props.children
  }
}

function AdminHeader({ activeTab }) {
  return (
    <header className="admin-hero">
      <div>
        <span className="admin-eyebrow">Admin Console</span>
        <h1>管理后台</h1>
        <p>{MODULE_DESCRIPTIONS[activeTab] || MODULE_DESCRIPTIONS.overview}</p>
      </div>
      <ShieldCheck size={34} />
    </header>
  )
}

function MetricCard({ label, value, hint, tone = 'default' }) {
  return <StatsCard className="admin-metric-card" label={label} value={value} hint={hint || ' '} tone={tone} />
}

function Section({ title, description, icon: Icon = Database, actions, children }) {
  return (
    <section className="admin-section">
      <div className="admin-section-heading">
        <div>
          <h2>{title}</h2>
          {description ? <p>{description}</p> : null}
        </div>
        {actions || <Icon size={20} />}
      </div>
      {children}
    </section>
  )
}

function ActionButton({ children, busy, ...props }) {
  return (
    <button className="secondary-button admin-user-membership-button" type="button" disabled={busy || props.disabled} {...props}>
      {busy ? '处理中...' : children}
    </button>
  )
}

function AdminTable({ columns, rows, emptyTitle = '暂无数据', emptyMessage = '当前没有可展示的数据。' }) {
  if (!rows.length) return <EmptyState title={emptyTitle} message={emptyMessage} />
  return (
    <div className="admin-table-wrap">
      <table className="admin-job-table">
        <thead>
          <tr>{columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.key || row.id || index}>
              {columns.map((column) => <td key={column.key}>{column.render ? column.render(row, index) : displayValue(row[column.key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AdminOverview({
  overviewData,
  userMetrics,
  rankingData,
  v2Data,
  vipOrders,
  supportData,
  jobsData,
  projectTemplateData,
  projectSourceData,
  onSelect,
}) {
  const overview = overviewData?.overview || {}
  const dailyItems = safeArray(rankingData?.daily?.items)
  const weeklyItems = safeArray(rankingData?.weekly?.items)
  const allTimeItems = safeArray(rankingData?.allTime?.items)
  const projectItems = [...dailyItems, ...weeklyItems, ...allTimeItems].slice(0, 10)
  const publishedCount = projectItems.filter((repo) => getRepoStatus(repo) === '已发布').length
  const readyCount = projectItems.filter((repo) => getRepoStatus(repo) !== '已发布').length
  const supportItems = safeArray(supportData?.items)
  const vipOrderItems = safeArray(vipOrders?.items)
  const jobItems = safeArray(jobsData?.items)
  const templates = safeArray(projectTemplateData?.items)
  const sourceItems = safeArray(projectSourceData?.items)
  const pendingSupport = supportItems.filter((item) => ['open', 'pending', 'processing', 'refund_requested'].includes(item.status || 'open'))
  const pendingOrders = vipOrderItems.filter((item) => ['submitted', 'pending', 'refund_requested'].includes(item.status || 'submitted'))
  const failedJobs = jobItems.filter((item) => item.status === 'failed')
  const draftTemplates = templates.filter((item) => ['draft', 'submitted', 'pending'].includes(item.reviewStatus || 'draft'))
  const pendingSources = sourceItems.filter((item) => !['converted', 'skipped', 'archived'].includes(item.convertStatus || 'pending'))
  const cards = [
    ['用户数', userMetrics?.total_users ?? overview.total_users, '全站注册用户', 'blue'],
    ['今日访问', overview.today_views ?? v2Data?.overview?.detailClicks, shanghaiDate(), 'green'],
    ['今日新增用户', userMetrics?.today_new_users, shanghaiDate(), 'green'],
    ['待处理客服', pendingSupport.length, '退款、额度、异常反馈', pendingSupport.length ? 'orange' : 'green'],
    ['待审核订单', pendingOrders.length, 'VIP 开通或退款', pendingOrders.length ? 'orange' : 'green'],
    ['待审核模板', draftTemplates.length + pendingSources.length, '模板草稿与外部素材', draftTemplates.length + pendingSources.length ? 'orange' : 'green'],
    ['失败任务', failedJobs.length, '需要排查的后台任务', failedJobs.length ? 'red' : 'green'],
    ['已发布项目', publishedCount, `待发布 ${number(readyCount)} 个`, 'blue'],
  ]

  const entryTabs = [
    ['support', '处理客服与退款', LifeBuoy],
    ['vipOrders', '审核 VIP 订单', FileText],
    ['studentGrowthStats', '查看成长运营', Bookmark],
    ['projectTemplates', '维护项目模板', FolderGit2],
    ['users', '查看用户权限', Users],
    ['jobs', '排查任务日志', Clock3],
  ]

  const todoItems = [
    {
      key: 'support',
      label: '客服与退款',
      value: pendingSupport.length,
      hint: pendingSupport[0]
        ? `${SUPPORT_TYPE_LABELS[pendingSupport[0].type] || '待处理工单'} / ${statusText(pendingSupport[0].status)}`
        : '当前没有待处理工单',
      action: '进入处理',
      icon: LifeBuoy,
      tone: pendingSupport.length ? 'urgent' : 'quiet',
    },
    {
      key: 'vipOrders',
      label: 'VIP 订单',
      value: pendingOrders.length,
      hint: pendingOrders[0]
        ? `${displayValue(pendingOrders[0].orderNo, '待审核订单')} / ${statusText(pendingOrders[0].status)}`
        : '当前没有待审核订单',
      action: '查看订单',
      icon: Crown,
      tone: pendingOrders.length ? 'urgent' : 'quiet',
    },
    {
      key: 'projectTemplates',
      label: '内容审核',
      value: draftTemplates.length + pendingSources.length,
      hint: `${number(draftTemplates.length)} 个模板草稿，${number(pendingSources.length)} 条外部素材`,
      action: '审核素材',
      icon: FolderGit2,
      tone: draftTemplates.length + pendingSources.length ? 'normal' : 'quiet',
    },
    {
      key: 'jobs',
      label: '任务异常',
      value: failedJobs.length,
      hint: failedJobs[0] ? `${JOB_LABELS[failedJobs[0].jobType] || '后台任务'} / ${statusText(failedJobs[0].status)}` : '后台任务运行正常',
      action: '查看日志',
      icon: Clock3,
      tone: failedJobs.length ? 'urgent' : 'quiet',
    },
  ]

  const growthChecklist = [
    ['成长目标', '统计当前已保存目标、方向选择和阶段分布', 'studentGrowthStats'],
    ['今日任务', '跟踪任务生成、完成、断点和次日留存', 'studentPracticeRecords'],
    ['成果资产', '观察项目、简历材料和可展示成果沉淀', 'projectTemplates'],
    ['学生反馈', '收集学生端困惑、卡点和产品建议', 'studentGrowthFeedback'],
  ]

  const columns = [
    { key: 'name', label: '项目名称', render: (repo) => getRepoName(repo) },
    { key: 'stars', label: 'Star', render: (repo) => compactNumber(repo.stars || repo.starCount) },
    { key: 'forks', label: 'Fork', render: (repo) => compactNumber(repo.forks || repo.forkCount) },
    { key: 'language', label: '主要语言', render: (repo) => displayValue(repo.language, '未设置') },
    { key: 'status', label: '发布状态', render: (repo) => getRepoStatus(repo) },
    { key: 'ai', label: 'AI 分析', render: (repo) => getAiStatus(repo) },
    { key: 'updatedAt', label: '更新时间', render: (repo) => dateTime(repo.updatedAt || repo.updated_at || repo.pushedAt) },
    { key: 'action', label: '操作', render: (repo) => repo.id ? <Link to={`/repos/${repo.id}`}>查看详情</Link> : '暂无操作' },
  ]

  return (
    <>
      <Section title="今日待办" description="先处理会影响用户体验和收入的事项，再查看数据。" icon={LifeBuoy}>
        <div className="admin-workbench-grid">
          {todoItems.map((item) => {
            const Icon = item.icon
            return (
              <button className={`admin-workbench-card is-${item.tone}`} key={item.key} type="button" onClick={() => onSelect(item.key)}>
                <span><Icon size={18} />{item.label}</span>
                <strong>{number(item.value)}</strong>
                <small>{item.hint}</small>
                <em>{item.action}</em>
              </button>
            )
          })}
        </div>
      </Section>

      <Section title="关键指标" description="用少量指标判断今天是否有异常，不在首页堆满报表。" icon={TrendingUp}>
        <div className="admin-metric-grid admin-core-metric-grid">
          {cards.map(([label, value, hint, tone]) => <MetricCard key={label} label={label} value={number(value)} hint={hint} tone={tone} />)}
        </div>
      </Section>

      <Section title="高频处理入口" description="按运营动作进入后台，而不是按技术模块找页面。" icon={Database}>
        <div className="admin-module-grid">
          {entryTabs.map(([key, label, Icon]) => (
            <button className="admin-module-card" key={key} type="button" onClick={() => onSelect(key)}>
              <Icon size={18} />
              <strong>{label}</strong>
              <span>{MODULE_DESCRIPTIONS[key]}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="学生成长运营看板" description="当前阶段先明确运营问题和数据接入方向，避免空白后台误导管理员。" icon={Bookmark}>
        <div className="admin-growth-ops-grid">
          {growthChecklist.map(([title, detail, key]) => (
            <button key={title} type="button" onClick={() => onSelect(key)}>
              <strong>{title}</strong>
              <span>{detail}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="项目榜单概览" description="项目名称、Star、Fork、发布状态和 AI 分析状态。" icon={Star}>
        <AdminTable columns={columns} rows={projectItems} emptyTitle="暂无项目榜单数据" />
      </Section>
    </>
  )
}

function RankingActions({ config, data, metrics, busyKey, onRun, actionResult, actionError }) {
  const items = safeArray(data?.items)
  const pageCompleted = items.filter((repo) => getAiStatus(repo) === '已分析').length
  const pageMissing = items.filter((repo) => getAiStatus(repo) === '未分析').length
  const completed = config.key === 'allTime'
    ? (metrics?.aiInsightProgress?.completed ?? pageCompleted)
    : pageCompleted
  const missing = config.key === 'allTime'
    ? (metrics?.aiInsightProgress?.missing ?? pageMissing)
    : pageMissing
  const published = items.filter((repo) => getRepoStatus(repo) === '已发布').length
  const updatedAt = items[0]?.updatedAt || items[0]?.updated_at || metrics?.updatedAt || metrics?.lastUpdatedAt
  const cards = [
    ['更新状态', items.length ? '已更新' : '暂无数据', `当前榜单数量 ${number(items.length)}`, 'blue'],
    ['发布状态', published ? '已发布' : '待发布', `已发布 ${number(published)} / 待发布 ${number(Math.max(0, items.length - published))}`, 'green'],
    ['当前榜单数量', number(items.length), config.title, 'default'],
    ['最后更新时间', dateTime(updatedAt), '榜单数据更新时间', 'default'],
    ['AI 分析完成数', number(completed), config.key === 'allTime' ? '全库已分析项目' : '当前榜单已分析项目', 'blue'],
    ['未完成 AI 分析', number(missing), config.key === 'allTime' ? '全库尚未生成 AI 洞察' : '当前榜单尚未分析', 'orange'],
    ['平均响应时间', displayValue(metrics?.averageResponseTime || metrics?.durationMs, '暂无数据'), '接口统计', 'default'],
  ]

  return (
    <div className="admin-ranking-actions">
      <div className="admin-metric-grid">
        {cards.map(([label, value, hint, tone]) => <MetricCard key={label} label={label} value={value} hint={hint} tone={tone} />)}
      </div>
      <div className="admin-action-row">
        <ActionButton busy={busyKey === `${config.key}:refresh`} onClick={() => onRun(config.key, 'refresh')}>刷新榜单</ActionButton>
        <ActionButton busy={busyKey === `${config.key}:publish`} onClick={() => onRun(config.key, 'publish')}>发布项目</ActionButton>
        {config.key === 'allTime' ? (
          <>
            <ActionButton busy={busyKey === `${config.key}:import`} onClick={() => onRun(config.key, 'import')}>同步数据</ActionButton>
            <ActionButton busy={busyKey === `${config.key}:insights`} onClick={() => onRun(config.key, 'insights')}>重新分析</ActionButton>
          </>
        ) : null}
        <Link className="secondary-button admin-action-button" to={config.viewPath}>查看详情</Link>
      </div>
      {actionError ? <div className="admin-notice error" role="alert">{actionError}</div> : null}
      {actionResult ? <div className="admin-notice success" role="status">操作已完成：{statusText(actionResult.status, '同步成功')}</div> : null}
    </div>
  )
}

function RankingTable({ items }) {
  const columns = [
    { key: 'index', label: '序号', render: (_repo, index) => index + 1 },
    { key: 'name', label: '项目名称', render: (repo) => <strong>{getRepoName(repo)}</strong> },
    { key: 'language', label: '主要语言', render: (repo) => displayValue(repo.language, '未设置') },
    { key: 'stars', label: 'Star', render: (repo) => compactNumber(repo.stars || repo.starCount) },
    { key: 'forks', label: 'Fork', render: (repo) => compactNumber(repo.forks || repo.forkCount) },
    { key: 'status', label: '状态', render: (repo) => getRepoStatus(repo) },
    { key: 'ai', label: 'AI 分析', render: (repo) => getAiStatus(repo) },
    { key: 'updatedAt', label: '更新时间', render: (repo) => dateTime(repo.updatedAt || repo.updated_at || repo.pushedAt) },
    { key: 'action', label: '操作', render: (repo) => repo.id ? <Link to={`/repos/${repo.id}`}>查看详情</Link> : '暂无操作' },
  ]
  return <AdminTable columns={columns} rows={safeArray(items)} emptyTitle="暂无榜单项目" />
}

function RankingPanel({ config, data, metrics, loading, error, busyKey, onRun, actionResult, actionError }) {
  const [aiFilter, setAiFilter] = useState('all')
  const items = safeArray(data?.items)
  const visibleItems = aiFilter === 'all'
    ? items
    : items.filter((repo) => aiFilter === 'completed' ? getAiStatus(repo) === '已分析' : getAiStatus(repo) !== '已分析')
  return (
    <Section title={config.title} description="查看项目的 AI 解析状态、发布状态和榜单数据。支持刷新榜单、发布项目和查看项目详情。" icon={TrendingUp}>
      <RankingActions
        config={config}
        data={data}
        metrics={metrics}
        busyKey={busyKey}
        onRun={onRun}
        actionResult={actionResult}
        actionError={actionError}
      />
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {config.key === 'allTime' ? (
        <div className="admin-ai-filter" aria-label="AI 分析状态筛选">
          <strong>当前页 AI 分析状态</strong>
          <button type="button" className={aiFilter === 'all' ? 'is-active' : ''} onClick={() => setAiFilter('all')}>全部 {items.length}</button>
          <button type="button" className={aiFilter === 'missing' ? 'is-active' : ''} onClick={() => setAiFilter('missing')}>未分析 {items.filter((repo) => getAiStatus(repo) !== '已分析').length}</button>
          <button type="button" className={aiFilter === 'completed' ? 'is-active' : ''} onClick={() => setAiFilter('completed')}>已分析 {items.filter((repo) => getAiStatus(repo) === '已分析').length}</button>
        </div>
      ) : null}
      {loading ? <LoadingState label="正在加载榜单数据" /> : <RankingTable items={visibleItems} />}
      {data?.pagination ? <Pagination pagination={data.pagination} disabled={loading} onPageChange={() => {}} /> : null}
    </Section>
  )
}

function UserManagementPanel({ data, metrics, loading, error, keyword, provider, sort, onKeywordChange, onProviderChange, onSortChange, onSearch, onMembershipChange }) {
  const payload = pagePayload(data)
  const cards = [
    ['用户数', metrics?.total_users, '全站注册用户', 'blue'],
    ['今日新增用户', metrics?.today_new_users, shanghaiDate(), 'green'],
    ['GitHub 登录用户', metrics?.github_users, 'OAuth 账号', 'default'],
    ['本地账号用户', metrics?.local_users, '账号密码登录', 'default'],
    ['管理员数量', metrics?.admin_users, '拥有后台权限', 'orange'],
  ]
  const columns = [
    { key: 'username', label: '用户名', render: (user) => displayValue(user.username || user.github_username, '未设置') },
    { key: 'email', label: '邮箱', render: (user) => displayValue(user.email, '未设置') },
    { key: 'createdAt', label: '注册时间', render: (user) => dateTime(user.created_at || user.createdAt) },
    { key: 'userType', label: '用户类型', render: (user) => user.is_admin ? '管理员' : '普通用户' },
    { key: 'vipStatus', label: 'VIP状态', render: userVipSummary },
    { key: 'vipPlan', label: 'VIP套餐', render: (user) => vipPlanLabel(user.vipPlanName || user.vipPlanId) },
    { key: 'vipStartsAt', label: 'VIP开始时间', render: (user) => dateOnly(user.vipStartsAt) },
    { key: 'vipExpiresAt', label: 'VIP结束时间', render: (user) => dateOnly(user.vipExpiresAt || user.membership_expires_at) },
    { key: 'vipRemainingDays', label: '剩余天数', render: (user) => user.vipRemainingDays ? `${user.vipRemainingDays} 天` : '-' },
    { key: 'usage', label: '使用次数', render: (user) => number(user.ai_analysis_count ?? user.aiAnalysisCount ?? user.analysis_count ?? user.today_github_analysis_count ?? 0) },
    {
      key: 'action',
      label: '操作',
      render: (user) => (
        <div className="admin-user-membership-actions">
          <ActionButton onClick={() => onMembershipChange(user, 'vip', 'monthly')}>月卡30天</ActionButton>
          <ActionButton onClick={() => onMembershipChange(user, 'vip', 'quarterly')}>季卡90天</ActionButton>
          <ActionButton onClick={() => onMembershipChange(user, 'vip', 'yearly')}>年卡365天</ActionButton>
          <ActionButton onClick={() => onMembershipChange(user, 'free', 0)}>取消 VIP</ActionButton>
        </div>
      ),
    },
  ]

  return (
    <Section title="用户管理" description="查看用户账号、会员状态、使用次数和权限。" icon={Users}>
      <div className="admin-metric-grid admin-user-metric-grid">
        {cards.map(([label, value, hint, tone]) => <MetricCard key={label} label={label} value={number(value)} hint={hint} tone={tone} />)}
      </div>
      <form className="admin-user-toolbar" onSubmit={onSearch}>
        <label className="admin-user-search">
          <Search size={16} />
          <input type="search" value={keyword} onChange={(event) => onKeywordChange(event.target.value)} placeholder="搜索用户名、邮箱或 GitHub 用户名" />
        </label>
        <select aria-label="登录方式筛选" value={provider} onChange={(event) => onProviderChange(event.target.value)}>
          <option value="">全部登录方式</option>
          <option value="local">本地账号</option>
          <option value="github">GitHub 登录</option>
        </select>
        <select aria-label="用户排序" value={sort} onChange={(event) => onSortChange(event.target.value)}>
          <option value="created_at">按注册时间</option>
          <option value="last_login_at">最后登录</option>
          <option value="analysis_count">AI 分析次数</option>
          <option value="favorite_count">收藏数</option>
        </select>
        <button className="primary-button admin-user-search-button" type="submit"><Search size={16} /> 搜索</button>
      </form>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载用户列表" /> : <AdminTable columns={columns} rows={payload.items} emptyTitle="暂无用户" />}
    </Section>
  )
}

function VipPanel({ memberships, logs, loading, error }) {
  const memberRows = pagePayload(memberships).items
  const logRows = pagePayload(logs).items
  const memberColumns = [
    { key: 'user', label: '会员用户', render: (item) => displayValue(item.user?.username || item.user?.email || item.userId, '未知用户') },
    { key: 'status', label: '权限状态', render: (item) => statusText(item.status, '未设置') },
    { key: 'expiresAt', label: '到期时间', render: (item) => dateTime(item.expiresAt || item.expires_at) },
    { key: 'source', label: '开通方式', render: (item) => displayValue(item.source || item.planId, '后台开通') },
    { key: 'action', label: '操作', render: () => '查看记录' },
  ]
  const logColumns = [
    { key: 'time', label: '创建时间', render: (item) => dateTime(item.createdAt || item.created_at) },
    { key: 'admin', label: '管理员', render: (item) => displayValue(item.admin?.username || item.adminUserId, '未知') },
    { key: 'user', label: '用户', render: (item) => displayValue(item.targetUser?.username || item.targetUserId, '未知用户') },
    { key: 'action', label: '操作', render: (item) => displayValue(item.action, '未设置') },
    { key: 'note', label: '处理备注', render: (item) => displayValue(item.reason || item.note, '暂无说明') },
  ]

  return (
    <Section title="VIP 管理" description="查看会员用户、权限状态、到期时间、开通方式和操作记录。" icon={Crown}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载 VIP 数据" /> : (
        <>
          <h3 className="admin-subtitle">会员列表</h3>
          <AdminTable columns={memberColumns} rows={memberRows} emptyTitle="暂无会员用户" />
          <h3 className="admin-subtitle">操作日志</h3>
          <AdminTable columns={logColumns} rows={logRows} emptyTitle="暂无操作日志" />
        </>
      )}
    </Section>
  )
}

function VipOrdersPanel({ data, loading, error, status, note, onStatusChange, onNoteChange, onAction }) {
  const rows = pagePayload(data).items
  const columns = [
    { key: 'orderNo', label: '订单号', render: (order) => displayValue(order.orderNo, '未知订单') },
    { key: 'user', label: '用户', render: (order) => displayValue(order.user?.username || order.user?.email || order.userId, '未知用户') },
    { key: 'plan', label: '套餐类型', render: (order) => vipPlanLabel(order.planName || order.planId) },
    { key: 'amount', label: '金额', render: (order) => order.amountYuan ? `¥${order.amountYuan}` : '未设置' },
    { key: 'paymentStatus', label: '支付状态', render: (order) => order.paymentStatus === 'paid' ? '已支付' : '待支付' },
    { key: 'status', label: '审核状态', render: (order) => statusText(order.status, '待审核') },
    { key: 'createdAt', label: '创建时间', render: (order) => dateTime(order.createdAt || order.created_at) },
    { key: 'activatedAt', label: '开通时间', render: (order) => dateTime(order.activatedAt || order.membershipStartsAt) },
    { key: 'expiresAt', label: '到期时间', render: (order) => dateTime(order.expiresAt || order.membershipExpiresAt) },
    { key: 'proof', label: '支付截图', render: (order) => order.proofImageUrl ? <a href={order.proofImageUrl} target="_blank" rel="noreferrer">查看截图</a> : '暂无截图' },
    {
      key: 'action',
      label: '操作',
      render: (order) => (
        <div className="admin-user-membership-actions">
          <ActionButton disabled={order.status !== 'submitted'} onClick={() => onAction(order.orderNo, 'confirm')}>通过</ActionButton>
          <ActionButton disabled={order.status === 'paid'} onClick={() => onAction(order.orderNo, 'reject')}>驳回</ActionButton>
          <ActionButton onClick={() => onAction(order.orderNo, 'refund')}>标记退款</ActionButton>
        </div>
      ),
    },
  ]

  return (
    <Section title="VIP 订单管理" description="审核 VIP 订单、付款截图和退款状态。" icon={FileText}>
      <div className="admin-user-toolbar">
        <select value={status} onChange={(event) => onStatusChange(event.target.value)} aria-label="订单状态">
          <option value="submitted">待审核</option>
          <option value="">全部订单</option>
          <option value="pending">待支付</option>
          <option value="paid">已通过</option>
          <option value="rejected">已驳回</option>
          <option value="refund_requested">申请退款</option>
          <option value="refunded">已退款</option>
        </select>
        <label className="admin-user-search">
          <Mail size={16} />
          <input value={note} onChange={(event) => onNoteChange(event.target.value)} placeholder="处理备注 / 驳回原因" />
        </label>
      </div>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载 VIP 订单" /> : <AdminTable columns={columns} rows={rows} emptyTitle="暂无 VIP 订单" />}
    </Section>
  )
}

function FeedbackPanel({ data, loading, error, title = '用户反馈', description = '查看用户反馈内容、联系方式和页面来源。' }) {
  const rows = pagePayload(data).items
  const columns = [
    { key: 'content', label: '反馈内容', render: (item) => <strong>{displayValue(item.content || item.message, '暂无说明')}</strong> },
    { key: 'contact', label: '联系方式', render: (item) => displayValue(item.contact, '未设置') },
    { key: 'page', label: '页面来源', render: (item) => displayValue(item.pageUrl || item.path, '未知') },
    { key: 'status', label: '状态', render: (item) => statusText(item.status) },
    { key: 'createdAt', label: '创建时间', render: (item) => dateTime(item.createdAt || item.created_at) },
    { key: 'action', label: '操作', render: () => '查看' },
  ]
  return (
    <Section title={title} description={description} icon={Mail}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载用户反馈" /> : <AdminTable columns={columns} rows={rows} emptyTitle="暂无用户反馈" />}
    </Section>
  )
}

function SupportPanel({ data, loading, error, selected, note, onSelect, onNoteChange, onAction }) {
  const rows = pagePayload(data).items
  const columns = [
    { key: 'title', label: '工单标题', render: (item) => displayValue(item.title || `工单 #${item.id}`, '未命名工单') },
    { key: 'user', label: '用户', render: (item) => displayValue(item.user?.username || item.user?.email || item.userId, '未知用户') },
    { key: 'type', label: '类型', render: (item) => SUPPORT_TYPE_LABELS[item.type] || displayValue(item.type, '其他') },
    { key: 'status', label: '状态', render: (item) => statusText(item.status) },
    { key: 'createdAt', label: '创建时间', render: (item) => dateTime(item.createdAt || item.created_at) },
    { key: 'note', label: '处理备注', render: (item) => displayValue(item.adminNote || item.note, '暂无说明') },
    { key: 'action', label: '操作', render: (item) => <ActionButton onClick={() => onSelect(item)}>查看</ActionButton> },
  ]

  return (
    <Section title="客服与退款" description="处理客服工单、退款申请和用户备注。" icon={LifeBuoy}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载客服工单" /> : <AdminTable columns={columns} rows={rows} emptyTitle="暂无客服工单" />}
      {selected ? (
        <article className="admin-support-detail">
          <header>
            <div>
              <h3>{displayValue(selected.title || `工单 #${selected.id}`, '工单详情')}</h3>
              <p>{SUPPORT_TYPE_LABELS[selected.type] || selected.type} / {statusText(selected.status)}</p>
            </div>
          </header>
          <dl className="admin-support-meta">
            <div><dt>用户</dt><dd>{displayValue(selected.user?.username || selected.user?.email || selected.userId, '未知用户')}</dd></div>
            <div><dt>订单号</dt><dd>{displayValue(selected.orderNo, '未设置')}</dd></div>
            <div><dt>联系方式</dt><dd>{displayValue(selected.contact, '未设置')}</dd></div>
            <div><dt>截图</dt><dd>{selected.screenshotUrl ? <a href={selected.screenshotUrl} target="_blank" rel="noreferrer">打开截图</a> : '暂无截图'}</dd></div>
          </dl>
          <p className="admin-support-description">{displayValue(selected.description, '暂无说明')}</p>
          <label className="admin-support-note">
            <span>处理备注</span>
            <textarea rows={4} value={note} onChange={(event) => onNoteChange(event.target.value)} placeholder="记录处理过程、退款说明或驳回原因" />
          </label>
          <div className="admin-support-actions">
            <ActionButton onClick={() => onAction('note')}>保存备注</ActionButton>
            <ActionButton onClick={() => onAction('processing')}>标记处理中</ActionButton>
            <ActionButton onClick={() => onAction('resolve')}>已解决</ActionButton>
            <ActionButton onClick={() => onAction('reject')}>驳回</ActionButton>
            <button className="primary-button" type="button" onClick={() => onAction('refund')}>标记退款</button>
          </div>
        </article>
      ) : null}
    </Section>
  )
}

function StudentGrowthPanel({ type, feedbackData, loading, error }) {
  if (type === 'feedback') {
    return <FeedbackPanel data={feedbackData} loading={loading} error={error} title="学生反馈" description="查看学生端提交的成长计划、任务和路线反馈。" />
  }

  const configs = {
    stats: {
      title: '成长计划统计',
      summary: '这里用于观察学生是否完成了从探索方向到每日成长的闭环。',
      cards: [
        ['待接入：成长目标', '规划中', '按方向、年级、阶段统计'],
        ['待接入：今日任务', '规划中', '生成、完成、断点数据'],
        ['待接入：学习路线', '规划中', '路线进入与节点完成情况'],
        ['待接入：成果资产', '规划中', '项目、简历、GitHub 材料沉淀'],
      ],
      next: [
        '接入 /api/growth/summary 的聚合统计，不读取单个学生隐私详情。',
        '优先展示活跃目标、今日任务完成率、成长阶段分布。',
        '把异常断点沉淀为运营待办，例如“生成目标后没有进入学习路线”。',
      ],
    },
    practice: {
      title: '实训打卡记录',
      summary: '这里用于看学生是否真的在推进任务，而不是只生成计划。',
      cards: [
        ['待接入：打卡记录', '规划中', '任务推进和提交记录'],
        ['待接入：今日任务', '规划中', '当天任务状态'],
        ['待接入：连续完成', '规划中', '连续学习和中断提醒'],
        ['待接入：成果提交', '规划中', '可展示产出沉淀'],
      ],
      next: [
        '从 student_daily_tasks 聚合任务状态，不新增业务接口时可先做只读统计。',
        '把“未开始、进行中、已完成、已跳过”做成队列。',
        '给管理员提供异常样本入口，帮助判断路线是否过难。',
      ],
    },
    competitions: {
      title: '竞赛/证书使用数据',
      summary: '这里用于判断竞赛证书是否真的帮助学生形成就业材料。',
      cards: [
        ['待接入：竞赛推荐', '规划中', '推荐点击和加入计划'],
        ['待接入：证书推荐', '规划中', '证书路径使用情况'],
        ['待接入：使用人数', '规划中', '参与规划的学生数'],
        ['待接入：简历转化', '规划中', '是否沉淀到简历资产'],
      ],
      next: [
        '把竞赛、证书从“资源展示”升级为“成果资产来源”。',
        '统计加入成长计划后的完成情况，而不是只看点击。',
        '和成果库、简历材料形成同一条运营链路。',
      ],
    },
  }
  const config = configs[type] || configs.stats

  return (
    <Section title={config.title} description={config.summary} icon={Bookmark}>
      <div className="admin-metric-grid admin-user-metric-grid">
        {config.cards.map(([label, value, hint]) => <MetricCard key={label} label={label} value={value} hint={hint} />)}
      </div>
      <div className="admin-roadmap-panel">
        <div>
          <strong>下一步接入建议</strong>
          <span>当前先保留入口和运营口径，避免后台出现无解释的空页面。</span>
        </div>
        <ol>
          {config.next.map((item) => <li key={item}>{item}</li>)}
        </ol>
      </div>
    </Section>
  )
}

function JobsPanel({ data, loading, error }) {
  const columns = [
    { key: 'jobType', label: '任务', render: (job) => JOB_LABELS[job.jobType] || displayValue(job.jobType, '未知任务') },
    { key: 'status', label: '状态', render: (job) => statusText(job.status) },
    { key: 'count', label: '数量', render: (job) => number(job.generatedCount ?? job.updatedCount ?? job.importedCount ?? 0) },
    { key: 'createdAt', label: '执行时间', render: (job) => dateTime(job.createdAt || job.created_at) },
  ]
  return (
    <Section title="任务日志" description="查看后台任务执行结果。" icon={Clock3}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载任务日志" /> : <AdminTable columns={columns} rows={pagePayload(data).items} emptyTitle="暂无任务日志" />}
    </Section>
  )
}

function HealthPanel({ data, loading, error }) {
  const rows = Object.entries(data || {}).map(([key, value]) => ({ key, name: key, value }))
  const columns = [
    { key: 'name', label: '检查项', render: (item) => displayValue(item.name, '未知') },
    { key: 'value', label: '状态', render: (item) => String(item.value ?? '未知') },
  ]
  return (
    <Section title="系统状态" description="查看系统状态和接口健康情况。" icon={ShieldCheck}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载系统状态" /> : <AdminTable columns={columns} rows={rows} emptyTitle="暂无系统状态" />}
    </Section>
  )
}

function SimpleModule({ title, description, actions = [] }) {
  return (
    <Section title={title} description={description} icon={Settings}>
      <EmptyState title={title} message={description || '暂无数据'} />
      {actions.length ? (
        <div className="admin-actions">
          {actions.map((action) => <Link className="secondary-button" to={action.to} key={action.to}>{action.label}</Link>)}
        </div>
      ) : null}
    </Section>
  )
}

function DeveloperToolsPanel({ data, loading, error, onRefresh }) {
  const tools = safeArray(data)
  const columns = [
    { key: 'name', label: '工具名称', render: (tool) => displayValue(tool.name || tool.title, '未命名工具') },
    { key: 'category', label: '分类', render: (tool) => displayValue(tool.category, '未设置') },
    { key: 'stars', label: 'Star', render: (tool) => compactNumber(tool.stars) },
    { key: 'url', label: '链接', render: (tool) => tool.url ? <a href={tool.url} target="_blank" rel="noreferrer">打开</a> : '暂无链接' },
  ]
  return (
    <Section
      title="开发者工具"
      description="查看开发者工具数据和跳转入口。"
      icon={Database}
      actions={<button className="secondary-button" type="button" onClick={onRefresh}><RefreshCw size={16} /> 刷新数据</button>}
    >
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {loading ? <LoadingState label="正在加载开发者工具" /> : <AdminTable columns={columns} rows={tools} emptyTitle="暂无开发者工具" />}
    </Section>
  )
}

const EMPTY_PROJECT_TEMPLATE_FORM = {
  title: '',
  summary: '',
  majorGroup: '通用',
  targetGoal: '作品集',
  difficulty: '入门',
  requiredSkills: '',
  deliverables: '',
  steps: '',
  resumeValue: '',
  reviewStatus: 'draft',
  isActive: false,
}

function listText(value) {
  if (Array.isArray(value)) return value.join('\n')
  return String(value || '')
}

function splitLines(value) {
  return String(value || '').split('\n').map((item) => item.trim()).filter(Boolean)
}

function ProjectTemplateAdminPanel({
  data,
  sources,
  loading,
  sourceLoading,
  error,
  form,
  notice,
  onFormChange,
  onEdit,
  onSubmit,
  onArchive,
  onImportGithub,
  onImportOpenAlex,
  onImportUrl,
  onConvert,
}) {
  const [sourceUrl, setSourceUrl] = useState('')
  const templates = safeArray(data?.items)
  const sourceItems = safeArray(sources?.items)
  const draftCount = templates.filter((item) => ['draft', 'submitted', 'pending'].includes(item.reviewStatus || 'draft')).length
  const activeCount = templates.filter((item) => item.isActive).length
  const sourcePendingCount = sourceItems.filter((item) => !['converted', 'skipped', 'archived'].includes(item.convertStatus || 'pending')).length
  const templateColumns = [
    { key: 'title', label: '模板名称', render: (item) => displayValue(item.title, '未命名模板') },
    { key: 'majorGroup', label: '专业方向', render: (item) => displayValue(item.majorGroup, '通用') },
    { key: 'targetGoal', label: '目标', render: (item) => displayValue(item.targetGoal, '作品集') },
    { key: 'reviewStatus', label: '审核状态', render: (item) => statusText(item.reviewStatus, '草稿') },
    { key: 'isActive', label: '展示状态', render: (item) => item.isActive ? '已上线' : '未上线' },
    {
      key: 'action',
      label: '操作',
      render: (item) => (
        <div className="admin-action-row">
          <button className="secondary-button" type="button" onClick={() => onEdit(item)}>编辑</button>
          <button className="secondary-button" type="button" onClick={() => onArchive(item.id)}>下架</button>
        </div>
      ),
    },
  ]
  const sourceColumns = [
    { key: 'sourceType', label: '来源', render: (item) => displayValue(item.sourceType, '未知') },
    { key: 'title', label: '素材标题', render: (item) => displayValue(item.title, '未命名素材') },
    { key: 'convertStatus', label: '转换状态', render: (item) => statusText(item.convertStatus, '待转换') },
    { key: 'fetchedAt', label: '采集时间', render: (item) => dateTime(item.fetchedAt) },
    {
      key: 'action',
      label: '操作',
      render: (item) => (
        <button className="secondary-button" type="button" onClick={() => onConvert(item.id)}>
          转为模板草稿
        </button>
      ),
    },
  ]

  return (
    <Section title="项目成长计划模板管理" description="管理基础模板库、外部公开素材和 AI 转换后的模板草稿。" icon={FolderGit2}>
      {error ? <div className="admin-notice error" role="alert">{error}</div> : null}
      {notice ? <div className="admin-notice success" role="status">{notice}</div> : null}

      <div className="admin-template-workflow">
        <article>
          <strong>{number(sourcePendingCount)}</strong>
          <span>外部素材待转换</span>
          <small>先进入素材池，不直接展示给学生。</small>
        </article>
        <article>
          <strong>{number(draftCount)}</strong>
          <span>模板草稿待审核</span>
          <small>审核通过后才可作为成长项目推荐。</small>
        </article>
        <article>
          <strong>{number(activeCount)}</strong>
          <span>已上线模板</span>
          <small>学生在成果库和成长计划中可见。</small>
        </article>
      </div>

      <div className="admin-template-layout">
        <form className="admin-project-template-form" onSubmit={onSubmit}>
          <div className="admin-form-intro">
            <strong>{form.id ? '编辑项目模板' : '新建项目模板'}</strong>
            <span>模板要能说明学生做什么、产出什么、如何写进简历。</span>
          </div>
          <label>模板名称<input value={form.title} onChange={(event) => onFormChange('title', event.target.value)} /></label>
          <label>专业方向<input value={form.majorGroup} onChange={(event) => onFormChange('majorGroup', event.target.value)} /></label>
          <label>目标<input value={form.targetGoal} onChange={(event) => onFormChange('targetGoal', event.target.value)} /></label>
          <label>难度<input value={form.difficulty} onChange={(event) => onFormChange('difficulty', event.target.value)} /></label>
          <label className="is-wide">摘要<textarea value={form.summary} onChange={(event) => onFormChange('summary', event.target.value)} /></label>
          <label>需要能力<textarea value={form.requiredSkills} onChange={(event) => onFormChange('requiredSkills', event.target.value)} placeholder="每行一个能力" /></label>
          <label>最终产出<textarea value={form.deliverables} onChange={(event) => onFormChange('deliverables', event.target.value)} placeholder="每行一个产出" /></label>
          <label className="is-wide">7 天步骤<textarea value={form.steps} onChange={(event) => onFormChange('steps', event.target.value)} placeholder="每行一个步骤" /></label>
          <label className="is-wide">简历价值<textarea value={form.resumeValue} onChange={(event) => onFormChange('resumeValue', event.target.value)} /></label>
          <label>审核状态
            <select value={form.reviewStatus} onChange={(event) => onFormChange('reviewStatus', event.target.value)}>
              <option value="draft">草稿</option>
              <option value="approved">已审核</option>
              <option value="archived">已下架</option>
            </select>
          </label>
          <label className="admin-project-template-check">
            <input type="checkbox" checked={form.isActive} onChange={(event) => onFormChange('isActive', event.target.checked)} />
            上线展示
          </label>
          <button className="primary-button" type="submit">保存模板</button>
        </form>

        <aside className="admin-template-review-note">
          <strong>审核重点</strong>
          <span>避免只像“AI 生成项目”。一个可上线模板至少要包含真实任务步骤、明确产出、可展示能力和简历表达价值。</span>
          <ul>
            <li>是否适合普通大学生完成</li>
            <li>是否能形成作品或简历材料</li>
            <li>是否有清晰的 7 天推进步骤</li>
          </ul>
        </aside>
      </div>

      {loading ? <LoadingState label="正在加载项目模板" /> : <AdminTable columns={templateColumns} rows={templates} emptyTitle="暂无项目模板" />}

      <div className="admin-section-heading admin-source-heading">
        <div>
          <h2>外部来源素材</h2>
          <p>外部公开数据只进入素材池，必须转成草稿并审核后才会展示给学生。</p>
        </div>
        <div className="admin-action-row">
          <button className="secondary-button" type="button" onClick={onImportGithub}>导入 GitHub 素材</button>
          <button className="secondary-button" type="button" onClick={onImportOpenAlex}>导入 OpenAlex 素材</button>
        </div>
      </div>
      <form className="admin-source-url-import" onSubmit={(event) => { event.preventDefault(); if (sourceUrl.trim()) onImportUrl(sourceUrl.trim()).then(() => setSourceUrl('')) }}>
        <label htmlFor="project-source-url">导入真实项目 URL</label>
        <input id="project-source-url" type="url" required value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} placeholder="https://grabcad.com/... / https://www.hackster.io/... / https://www.kaggle.com/..." />
        <button className="primary-button" type="submit">解析并进入审核池</button>
        <small>仅保存公开元数据和外部链接，不复制代码、CAD 文件、图片或数据集。</small>
      </form>
      {sourceLoading ? <LoadingState label="正在加载外部素材" /> : <AdminTable columns={sourceColumns} rows={sourceItems} emptyTitle="暂无外部素材" />}
    </Section>
  )
}

function AdminPage() {
  const [activeTab, setActiveTab] = useState('overview')
  const [notice, setNotice] = useState('')

  const [operationsData, setOperationsData] = useState(null)
  const [, setOperationsError] = useState('')
  const [v2Data, setV2Data] = useState(null)
  const [v2Error, setV2Error] = useState('')
  const [rankingMetrics, setRankingMetrics] = useState(null)
  const [rankingMetricsError, setRankingMetricsError] = useState('')
  const [rankingData, setRankingData] = useState({ daily: pagePayload(), weekly: pagePayload(), allTime: pagePayload() })
  const [rankingLoading, setRankingLoading] = useState(false)
  const [rankingError, setRankingError] = useState('')
  const [rankingBusy, setRankingBusy] = useState('')
  const [rankingActionError, setRankingActionError] = useState('')
  const [rankingActionResult, setRankingActionResult] = useState(null)

  const [usersData, setUsersData] = useState(pagePayload())
  const [userMetrics, setUserMetrics] = useState(null)
  const [usersLoading, setUsersLoading] = useState(false)
  const [usersError, setUsersError] = useState('')
  const [keyword, setKeyword] = useState('')
  const [provider, setProvider] = useState('')
  const [sort, setSort] = useState('created_at')

  const [vipMemberships, setVipMemberships] = useState(pagePayload())
  const [vipLogs, setVipLogs] = useState(pagePayload())
  const [vipLoading, setVipLoading] = useState(false)
  const [vipError, setVipError] = useState('')

  const [vipOrders, setVipOrders] = useState(pagePayload())
  const [vipOrdersLoading, setVipOrdersLoading] = useState(false)
  const [vipOrdersError, setVipOrdersError] = useState('')
  const [vipOrderStatus, setVipOrderStatus] = useState('submitted')
  const [vipOrderNote, setVipOrderNote] = useState('')

  const [feedbackData, setFeedbackData] = useState(pagePayload())
  const [feedbackLoading, setFeedbackLoading] = useState(false)
  const [feedbackError, setFeedbackError] = useState('')

  const [supportData, setSupportData] = useState(pagePayload())
  const [supportLoading, setSupportLoading] = useState(false)
  const [supportError, setSupportError] = useState('')
  const [selectedSupport, setSelectedSupport] = useState(null)
  const [supportNote, setSupportNote] = useState('')

  const [jobsData, setJobsData] = useState(pagePayload())
  const [jobsLoading, setJobsLoading] = useState(false)
  const [jobsError, setJobsError] = useState('')
  const [healthData, setHealthData] = useState({})
  const [healthLoading, setHealthLoading] = useState(false)
  const [healthError, setHealthError] = useState('')
  const [toolsData, setToolsData] = useState([])
  const [toolsLoading, setToolsLoading] = useState(false)
  const [toolsError, setToolsError] = useState('')
  const [projectTemplateData, setProjectTemplateData] = useState(pagePayload())
  const [projectSourceData, setProjectSourceData] = useState(pagePayload())
  const [projectTemplateLoading, setProjectTemplateLoading] = useState(false)
  const [projectSourceLoading, setProjectSourceLoading] = useState(false)
  const [projectTemplateError, setProjectTemplateError] = useState('')
  const [projectTemplateNotice, setProjectTemplateNotice] = useState('')
  const [projectTemplateForm, setProjectTemplateForm] = useState(EMPTY_PROJECT_TEMPLATE_FORM)

  useEffect(() => {
    setPageMetadata({
      title: '管理后台 - 青途 AI',
      description: '管理用户、VIP 订单、榜单、AI 分析和学生成长数据。',
      robots: NOINDEX_ROBOTS,
    })
  }, [])

  const loadOperations = useCallback(async (signal) => {
    setOperationsError('')
    try {
      setOperationsData(await getAdminAnalyticsOverview(signal))
    } catch (error) {
      if (error.name !== 'AbortError') setOperationsError(errorMessage(error))
    }
  }, [])

  const loadV2 = useCallback(async (signal) => {
    setV2Error('')
    try {
      const [v2, topRepos] = await Promise.allSettled([
        getAdminV2Analytics(signal),
        getAdminAnalyticsTopRepos(signal),
      ])
      setV2Data({
        ...(v2.status === 'fulfilled' ? v2.value : {}),
        topRepos: topRepos.status === 'fulfilled' ? topRepos.value : null,
      })
      if (v2.status === 'rejected') setV2Error(errorMessage(v2.reason))
    } catch (error) {
      if (error.name !== 'AbortError') setV2Error(errorMessage(error))
    }
  }, [])

  const loadRankingMetrics = useCallback(async (signal) => {
    setRankingMetricsError('')
    try {
      setRankingMetrics(await getRankingMetrics(shanghaiDate(), signal))
    } catch (error) {
      if (error.name !== 'AbortError') {
        setRankingMetrics({})
        setRankingMetricsError(errorMessage(error))
      }
    }
  }, [])

  const loadRankingList = useCallback(async (key, signal) => {
    setRankingLoading(true)
    setRankingError('')
    try {
      const loaders = {
        daily: () => getAdminDailyRanking(shanghaiDate(), 1, 10, signal),
        weekly: () => getAdminWeeklyRanking(shanghaiDate(), 1, 10, signal),
        allTime: () => getAdminAllTimeRanking(1, 10, signal),
      }
      const result = await loaders[key]()
      setRankingData((current) => ({ ...current, [key]: pagePayload(result) }))
    } catch (error) {
      if (error.name !== 'AbortError') {
        setRankingData((current) => ({ ...current, [key]: pagePayload() }))
        setRankingError(errorMessage(error))
      }
    } finally {
      if (!signal?.aborted) setRankingLoading(false)
    }
  }, [])

  const loadUsers = useCallback(async (signal) => {
    setUsersLoading(true)
    setUsersError('')
    try {
      const [users, metrics] = await Promise.all([
        getAdminUsers({ page: 1, pageSize: 20, keyword, provider, sort }, signal),
        getAdminUserMetrics(signal),
      ])
      setUsersData(pagePayload(users))
      setUserMetrics(metrics)
    } catch (error) {
      if (error.name !== 'AbortError') setUsersError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setUsersLoading(false)
    }
  }, [keyword, provider, sort])

  const loadVip = useCallback(async (signal) => {
    setVipLoading(true)
    setVipError('')
    try {
      const [memberships, logs] = await Promise.allSettled([
        getAdminVipMemberships({ page: 1, pageSize: 50 }, signal),
        getAdminVipLogs({ page: 1, pageSize: 50 }, signal),
      ])
      setVipMemberships(memberships.status === 'fulfilled' ? pagePayload(memberships.value) : pagePayload())
      setVipLogs(logs.status === 'fulfilled' ? pagePayload(logs.value) : pagePayload())
      if (memberships.status === 'rejected' || logs.status === 'rejected') setVipError('部分 VIP 数据加载失败，请稍后重试。')
    } catch (error) {
      if (error.name !== 'AbortError') setVipError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setVipLoading(false)
    }
  }, [])

  const loadVipOrders = useCallback(async (signal) => {
    setVipOrdersLoading(true)
    setVipOrdersError('')
    try {
      setVipOrders(pagePayload(await getAdminVipOrders({ page: 1, pageSize: 50, status: vipOrderStatus }, signal)))
    } catch (error) {
      if (error.name !== 'AbortError') setVipOrdersError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setVipOrdersLoading(false)
    }
  }, [vipOrderStatus])

  const loadFeedback = useCallback(async (signal) => {
    setFeedbackLoading(true)
    setFeedbackError('')
    try {
      setFeedbackData(pagePayload(await getAdminFeedback({ page: 1, pageSize: 80 }, signal)))
    } catch (error) {
      if (error.name !== 'AbortError') setFeedbackError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setFeedbackLoading(false)
    }
  }, [])

  const loadSupport = useCallback(async (signal) => {
    setSupportLoading(true)
    setSupportError('')
    try {
      const result = pagePayload(await getAdminSupportTickets({ page: 1, pageSize: 50 }, signal))
      setSupportData(result)
      setSelectedSupport((current) => result.items.find((item) => item.id === current?.id) || result.items[0] || null)
    } catch (error) {
      if (error.name !== 'AbortError') setSupportError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setSupportLoading(false)
    }
  }, [])

  const loadJobs = useCallback(async (signal) => {
    setJobsLoading(true)
    setJobsError('')
    try {
      setJobsData(pagePayload(await getRankingJobs(1, 20, signal)))
    } catch (error) {
      if (error.name !== 'AbortError') setJobsError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setJobsLoading(false)
    }
  }, [])

  const loadHealth = useCallback(async (signal) => {
    setHealthLoading(true)
    setHealthError('')
    try {
      setHealthData(await getRankingHealth(signal) || {})
    } catch (error) {
      if (error.name !== 'AbortError') setHealthError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setHealthLoading(false)
    }
  }, [])

  const loadTools = useCallback(async (signal) => {
    setToolsLoading(true)
    setToolsError('')
    try {
      const result = await getTools({ page: 1, pageSize: 12 }, signal)
      setToolsData(safeArray(result?.items || result))
    } catch (error) {
      if (error.name !== 'AbortError') setToolsError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setToolsLoading(false)
    }
  }, [])

  const loadProjectTemplates = useCallback(async (signal) => {
    setProjectTemplateLoading(true)
    setProjectTemplateError('')
    try {
      setProjectTemplateData(pagePayload(await getAdminProjectTemplates(signal)))
    } catch (error) {
      if (error.name !== 'AbortError') setProjectTemplateError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setProjectTemplateLoading(false)
    }
  }, [])

  const loadProjectSources = useCallback(async (signal) => {
    setProjectSourceLoading(true)
    try {
      setProjectSourceData(pagePayload(await getAdminExternalSourceItems(signal)))
    } catch (error) {
      if (error.name !== 'AbortError') setProjectTemplateError(errorMessage(error))
    } finally {
      if (!signal?.aborted) setProjectSourceLoading(false)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    if (['overview', 'operations'].includes(activeTab)) loadOperations(controller.signal)
    if (['overview', 'v2'].includes(activeTab)) loadV2(controller.signal)
    if (['overview', 'analytics', 'daily', 'weekly', 'allTime'].includes(activeTab)) loadRankingMetrics(controller.signal)
    if (activeTab === 'overview') {
      loadUsers(controller.signal)
      loadVipOrders(controller.signal)
      loadSupport(controller.signal)
      loadFeedback(controller.signal)
      loadJobs(controller.signal)
      loadProjectTemplates(controller.signal)
      loadProjectSources(controller.signal)
      loadRankingList('daily', controller.signal)
      loadRankingList('weekly', controller.signal)
      loadRankingList('allTime', controller.signal)
    }
    if (['daily', 'weekly', 'allTime'].includes(activeTab)) loadRankingList(activeTab, controller.signal)
    if (activeTab === 'users') loadUsers(controller.signal)
    if (activeTab === 'vip') loadVip(controller.signal)
    if (activeTab === 'vipOrders') loadVipOrders(controller.signal)
    if (['feedback', 'studentGrowthFeedback'].includes(activeTab)) loadFeedback(controller.signal)
    if (activeTab === 'support') loadSupport(controller.signal)
    if (activeTab === 'jobs') loadJobs(controller.signal)
    if (activeTab === 'health') loadHealth(controller.signal)
    if (activeTab === 'developerTools') loadTools(controller.signal)
    if (activeTab === 'projectTemplates') {
      loadProjectTemplates(controller.signal)
      loadProjectSources(controller.signal)
    }
    return () => controller.abort()
  }, [activeTab, loadFeedback, loadHealth, loadJobs, loadOperations, loadProjectSources, loadProjectTemplates, loadRankingList, loadRankingMetrics, loadSupport, loadTools, loadUsers, loadV2, loadVip, loadVipOrders])

  useEffect(() => {
    setSupportNote(selectedSupport?.adminNote || '')
  }, [selectedSupport])

  async function runRankingAction(key, action) {
    const busy = `${key}:${action}`
    setRankingBusy(busy)
    setRankingActionError('')
    setRankingActionResult(null)
    try {
      const date = shanghaiDate()
      const runners = {
        daily: {
          refresh: () => generateDailyRanking({ date, force: true }),
          publish: () => publishDailyRanking({ date }),
        },
        weekly: {
          refresh: () => refreshWeeklyRanking({ date }),
          publish: () => publishWeeklyRanking({ date }),
        },
        allTime: {
          refresh: () => refreshAllTimeRanking({ date }),
          publish: () => refreshAllTimeRanking({ date, publish: true }),
          import: () => importAllTimeProjects({ date }),
          insights: () => generateAllTimeInsights({ date }),
        },
      }
      const result = await runners[key]?.[action]?.()
      setRankingActionResult(result || { status: 'success' })
      await loadRankingList(key)
    } catch (error) {
      setRankingActionError(errorMessage(error))
    } finally {
      setRankingBusy('')
    }
  }

  async function changeMembership(user, tier, planId) {
    try {
      await updateAdminUserMembership(user.id, tier === 'vip' ? { tier, planId } : { tier })
      setNotice(tier === 'vip' ? `${vipPlanLabel(planId)} 已开通` : 'VIP 已取消')
      await loadUsers()
      await loadVip()
    } catch (error) {
      setNotice(errorMessage(error))
    }
  }

  async function runVipOrderAction(orderNo, action) {
    try {
      const runners = {
        confirm: confirmAdminVipOrder,
        reject: rejectAdminVipOrder,
        refund: markRefundedAdminVipOrder,
      }
      await runners[action](orderNo, { adminNote: vipOrderNote })
      setNotice('VIP 订单已更新')
      await loadVipOrders()
    } catch (error) {
      setVipOrdersError(errorMessage(error))
    }
  }

  async function runSupportAction(action) {
    if (!selectedSupport) return
    try {
      const payload = { adminNote: supportNote }
      const runners = {
        note: updateAdminSupportTicket,
        processing: (id, body) => updateAdminSupportTicket(id, { ...body, status: 'processing' }),
        resolve: resolveAdminSupportTicket,
        reject: rejectAdminSupportTicket,
        refund: markRefundedAdminSupportTicket,
      }
      await runners[action](selectedSupport.id, payload)
      setNotice('客服工单已更新')
      await loadSupport()
    } catch (error) {
      setSupportError(errorMessage(error))
    }
  }

  function handleUserSearch(event) {
    event.preventDefault()
    loadUsers()
  }

  function updateProjectTemplateForm(key, value) {
    setProjectTemplateForm((current) => ({ ...current, [key]: value }))
  }

  function editProjectTemplate(item) {
    setProjectTemplateForm({
      id: item.id,
      title: item.title || '',
      summary: item.summary || '',
      majorGroup: item.majorGroup || '通用',
      targetGoal: item.targetGoal || '作品集',
      difficulty: item.difficulty || '入门',
      requiredSkills: listText(item.requiredSkills),
      deliverables: listText(item.deliverables),
      steps: listText(item.steps),
      resumeValue: item.resumeValue || '',
      reviewStatus: item.reviewStatus || 'draft',
      isActive: Boolean(item.isActive),
    })
  }

  async function submitProjectTemplate(event) {
    event.preventDefault()
    setProjectTemplateError('')
    setProjectTemplateNotice('')
    const payload = {
      ...projectTemplateForm,
      requiredSkills: splitLines(projectTemplateForm.requiredSkills),
      deliverables: splitLines(projectTemplateForm.deliverables),
      steps: splitLines(projectTemplateForm.steps),
    }
    try {
      if (payload.id) await updateAdminProjectTemplate(payload.id, payload)
      else await createAdminProjectTemplate(payload)
      setProjectTemplateNotice('项目模板已保存')
      setProjectTemplateForm(EMPTY_PROJECT_TEMPLATE_FORM)
      await loadProjectTemplates()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  async function archiveProjectTemplate(templateId) {
    try {
      await archiveAdminProjectTemplate(templateId)
      setProjectTemplateNotice('项目模板已下架')
      await loadProjectTemplates()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  async function importGithubProjectSources() {
    try {
      const result = await importAdminGithubSourceItems({ limit: 20 })
      setProjectTemplateNotice(`GitHub 素材已导入 ${number(result.imported)} 条`)
      await loadProjectSources()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  async function importOpenAlexProjectSources() {
    try {
      const result = await importAdminOpenAlexSourceItems({ query: 'student research project' })
      setProjectTemplateNotice(`OpenAlex 素材已导入 ${number(result.imported)} 条`)
      await loadProjectSources()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  async function importProjectSourceUrl(url) {
    setProjectTemplateError('')
    try {
      const result = await importAdminProjectSourceUrl(url)
      setProjectTemplateNotice(`${result.item?.sourcePlatform || '外部'} 项目已进入审核池`)
      await loadProjectSources()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  async function convertProjectSource(sourceId) {
    try {
      await convertAdminSourceItemToTemplate(sourceId)
      setProjectTemplateNotice('外部素材已转换为模板草稿，请审核后上线')
      await loadProjectTemplates()
      await loadProjectSources()
    } catch (error) {
      setProjectTemplateError(errorMessage(error))
    }
  }

  const rankingConfigs = {
    daily: { key: 'daily', title: '每日榜管理', viewPath: '/' },
    weekly: { key: 'weekly', title: '每周榜管理', viewPath: '/weekly' },
    allTime: { key: 'allTime', title: '总榜管理', viewPath: '/all-time' },
  }

  const normalizedNotice = notice ? normalizeDisplayText(notice, '') : ''

  function renderPanel() {
    if (activeTab === 'overview') {
      return (
        <AdminOverview
          overviewData={operationsData}
          userMetrics={userMetrics}
          rankingMetrics={rankingMetrics}
          rankingData={rankingData}
          v2Data={v2Data}
          vipOrders={vipOrders}
          supportData={supportData}
          feedbackData={feedbackData}
          jobsData={jobsData}
          projectTemplateData={projectTemplateData}
          projectSourceData={projectSourceData}
          onSelect={setActiveTab}
        />
      )
    }
    if (['daily', 'weekly', 'allTime'].includes(activeTab)) {
      const config = rankingConfigs[activeTab]
      return (
        <RankingPanel
          config={config}
          data={rankingData[activeTab]}
          metrics={rankingMetrics}
          loading={rankingLoading}
          error={rankingError || rankingMetricsError}
          busyKey={rankingBusy}
          onRun={runRankingAction}
          actionResult={rankingActionResult}
          actionError={rankingActionError}
        />
      )
    }
    if (activeTab === 'users') {
      return (
        <UserManagementPanel
          data={usersData}
          metrics={userMetrics}
          loading={usersLoading}
          error={usersError}
          keyword={keyword}
          provider={provider}
          sort={sort}
          onKeywordChange={setKeyword}
          onProviderChange={setProvider}
          onSortChange={setSort}
          onSearch={handleUserSearch}
          onMembershipChange={changeMembership}
        />
      )
    }
    if (activeTab === 'vip') return <VipPanel memberships={vipMemberships} logs={vipLogs} loading={vipLoading} error={vipError} />
    if (activeTab === 'vipOrders') {
      return (
        <VipOrdersPanel
          data={vipOrders}
          loading={vipOrdersLoading}
          error={vipOrdersError}
          status={vipOrderStatus}
          note={vipOrderNote}
          onStatusChange={setVipOrderStatus}
          onNoteChange={setVipOrderNote}
          onAction={runVipOrderAction}
        />
      )
    }
    if (activeTab === 'support') {
      return (
        <SupportPanel
          data={supportData}
          loading={supportLoading}
          error={supportError}
          selected={selectedSupport}
          note={supportNote}
          onSelect={setSelectedSupport}
          onNoteChange={setSupportNote}
          onAction={runSupportAction}
        />
      )
    }
    if (activeTab === 'feedback') return <FeedbackPanel data={feedbackData} loading={feedbackLoading} error={feedbackError} />
    if (activeTab === 'studentGrowthStats') return <StudentGrowthPanel type="stats" />
    if (activeTab === 'studentGrowthFeedback') return <StudentGrowthPanel type="feedback" feedbackData={feedbackData} loading={feedbackLoading} error={feedbackError} />
    if (activeTab === 'studentPracticeRecords') return <StudentGrowthPanel type="practice" />
    if (activeTab === 'studentCompetitionStats') return <StudentGrowthPanel type="competitions" />
    if (activeTab === 'projectTemplates') {
      return (
        <ProjectTemplateAdminPanel
          data={projectTemplateData}
          sources={projectSourceData}
          loading={projectTemplateLoading}
          sourceLoading={projectSourceLoading}
          error={projectTemplateError}
          notice={projectTemplateNotice}
          form={projectTemplateForm}
          onFormChange={updateProjectTemplateForm}
          onEdit={editProjectTemplate}
          onSubmit={submitProjectTemplate}
          onArchive={archiveProjectTemplate}
          onImportGithub={importGithubProjectSources}
          onImportOpenAlex={importOpenAlexProjectSources}
          onImportUrl={importProjectSourceUrl}
          onConvert={convertProjectSource}
        />
      )
    }
    if (activeTab === 'jobs') return <JobsPanel data={jobsData} loading={jobsLoading} error={jobsError} />
    if (activeTab === 'health') return <HealthPanel data={healthData} loading={healthLoading} error={healthError} />
    if (activeTab === 'developerTools') return <DeveloperToolsPanel data={toolsData} loading={toolsLoading} error={toolsError} onRefresh={() => loadTools()} />
    if (activeTab === 'operations') return (
      <AdminOverview
        overviewData={operationsData}
        userMetrics={userMetrics}
        rankingMetrics={rankingMetrics}
        rankingData={rankingData}
        v2Data={v2Data}
        vipOrders={vipOrders}
        supportData={supportData}
        feedbackData={feedbackData}
        jobsData={jobsData}
        projectTemplateData={projectTemplateData}
        projectSourceData={projectSourceData}
        onSelect={setActiveTab}
      />
    )
    if (activeTab === 'v2') return <SimpleModule title="V2 统计" description={v2Error || '查看新版工具页的用户行为统计。'} />
    if (activeTab === 'analytics') return <SimpleModule title="AI 分析状态" description={rankingMetricsError || '查看 AI 分析完成度、榜单健康度和接口状态。'} />
    if (activeTab === 'repos') return <RankingPanel config={rankingConfigs.allTime} data={rankingData.allTime} metrics={rankingMetrics} loading={rankingLoading} error={rankingError} busyKey={rankingBusy} onRun={runRankingAction} actionResult={rankingActionResult} actionError={rankingActionError} />
    if (activeTab === 'settings') return <SimpleModule title="系统设置" description="系统设置入口已保留，后续接入配置接口后展示详细设置。" />
    return <SimpleModule title="系统设置" description="系统设置入口已保留，后续接入配置接口后展示详细设置。" />
  }

  return (
    <div className="page-container admin-page">
      <AdminHeader activeTab={activeTab} />
      {normalizedNotice ? <div className="admin-notice success" role="status">{normalizedNotice}</div> : null}

      <nav className="admin-tabs" aria-label="后台核心模块">
        {TABS.map(([key, label]) => (
          <button key={key} className={activeTab === key ? 'is-active' : ''} type="button" onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </nav>

      <section className="admin-tab-groups" aria-label="后台模块分组">
        {ADMIN_GROUPS.map((group) => (
          <div className="admin-tab-group" key={group.title}>
            <strong>{group.title}</strong>
            <div>
              {group.tabs.map(([key, label]) => (
                <button key={key} className={activeTab === key ? 'is-active' : ''} type="button" onClick={() => setActiveTab(key)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>

      <AdminErrorBoundary>{renderPanel()}</AdminErrorBoundary>
    </div>
  )
}

export default AdminPage
